class UxProduct extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('.ux-product__viewport');
    this.slides = Array.from(this.querySelectorAll('.ux-product__slide'));
    this.thumbs = Array.from(this.querySelectorAll('.ux-product__thumb, .ux-product__dot'));
    this.desktopMql = window.matchMedia('(min-width: 990px)');
    this.reducedMotionMql = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.autoplayEnabled = this.dataset.autoplayEnabled !== 'false';
    this.autoplayLoop = this.dataset.autoplayLoop !== 'false';
    const seconds = parseFloat(this.dataset.autoplaySeconds);
    this.autoplayMs = (Number.isFinite(seconds) && seconds > 0 ? seconds : 5) * 1000;
    const resumeSeconds = parseFloat(this.dataset.autoplayResumeSeconds);
    this.resumeDelayMs = (Number.isFinite(resumeSeconds) && resumeSeconds > 0 ? resumeSeconds : 4) * 1000;
    const transitionMs = parseFloat(this.dataset.transitionMs);
    this.transitionMs = Number.isFinite(transitionMs) && transitionMs > 0 ? transitionMs : 600;
    this.autoplayEasing = this.dataset.autoplayEasing === 'linear' ? 'linear' : 'ease-out';
    this.autoplayDirection = this.dataset.autoplayDirection === 'ltr' ? 'ltr' : 'rtl';
    this.scrollAnimFrame = null;

    this.activeIdx = 0;
    this.autoplayTimer = null;
    this.resumeTimer = null;

    this.thumbs.forEach((thumb, idx) => {
      thumb.addEventListener('click', () => {
        this.pauseAutoplay();
        this.scheduleResume();
        this.select(idx);
      });
    });

    if (this.viewport && this.slides.length > 1) {
      this.ghostEnd = this.slides[0].cloneNode(true);
      this.ghostEnd.classList.add('ux-product__slide--ghost');
      this.ghostEnd.classList.remove('is-active');
      this.ghostEnd.setAttribute('aria-hidden', 'true');
      this.ghostEnd.removeAttribute('data-media-index');
      this.viewport.appendChild(this.ghostEnd);

      this.ghostStart = this.slides[this.slides.length - 1].cloneNode(true);
      this.ghostStart.classList.add('ux-product__slide--ghost');
      this.ghostStart.classList.remove('is-active');
      this.ghostStart.setAttribute('aria-hidden', 'true');
      this.ghostStart.removeAttribute('data-media-index');
      this.viewport.insertBefore(this.ghostStart, this.viewport.firstChild);

      requestAnimationFrame(() => {
        if (this.isSliderMode() && this.slides[0]) {
          this.snapTo(this.centerOffsetFor(this.slides[0]));
        }
      });
    }

    if (this.viewport && 'IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!this.isSliderMode()) return;
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
              const idx = this.slides.indexOf(entry.target);
              if (idx >= 0) {
                this.activeIdx = idx;
                this.setActiveThumb(idx);
              }
            }
          });
        },
        { root: this.viewport, threshold: [0.6] }
      );
      this.slides.forEach((slide) => this.observer.observe(slide));
    }

    if (this.viewport) {
      this.touchStartX = null;
      this.viewport.addEventListener(
        'touchstart',
        (e) => {
          this.pauseAutoplay();
          this.touchStartX = e.touches[0]?.clientX ?? null;
        },
        { passive: true }
      );
      this.viewport.addEventListener(
        'touchend',
        (e) => {
          this.handleSwipeEnd(e);
          this.scheduleResume();
        },
        { passive: true }
      );
      this.viewport.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse') return;
        this.pauseAutoplay();
        this.scheduleResume();
      });
    }

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.pauseAutoplay();
      else if (!this.desktopMql.matches) this.startAutoplay();
    });

    this.desktopMql.addEventListener('change', () => {
      this.handleLayoutChange();
    });

    if ('ResizeObserver' in window && this.viewport) {
      this.resizeObserver = new ResizeObserver(() => this.handleLayoutChange());
      this.resizeObserver.observe(this.viewport);
    }

    requestAnimationFrame(() => this.startAutoplay());
  }

  handleLayoutChange() {
    if (this.isSliderMode()) {
      if (!this.autoplayTimer) this.startAutoplay();
    } else {
      this.pauseAutoplay();
    }
  }

  handleSwipeEnd(event) {
    if (!this.autoplayLoop) return;
    if (this.touchStartX == null) return;
    const endX = event.changedTouches[0]?.clientX;
    if (endX == null) return;
    const dx = endX - this.touchStartX;
    this.touchStartX = null;
    const SWIPE_THRESHOLD = 40;
    const lastIdx = this.slides.length - 1;
    if (dx < -SWIPE_THRESHOLD && this.activeIdx === lastIdx) {
      this.wrapToFirst();
    } else if (dx > SWIPE_THRESHOLD && this.activeIdx === 0) {
      this.wrapToLast();
    }
  }

  isSliderMode() {
    if (!this.viewport) return false;
    return this.viewport.scrollWidth > this.viewport.clientWidth + 10;
  }

  startAutoplay() {
    if (!this.autoplayEnabled) return;
    if (this.autoplayTimer) return;
    if (this.slides.length <= 1) return;
    if (!this.isSliderMode()) return;
    this.autoplayTimer = setInterval(() => this.advance(), this.autoplayMs);
  }

  pauseAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
    if (this.resumeTimer) {
      clearTimeout(this.resumeTimer);
      this.resumeTimer = null;
    }
  }

  scheduleResume() {
    if (this.resumeTimer) clearTimeout(this.resumeTimer);
    this.resumeTimer = setTimeout(() => this.startAutoplay(), this.resumeDelayMs);
  }

  advance() {
    if (!this.isSliderMode()) {
      this.pauseAutoplay();
      return;
    }
    if (!this.slides.length) return;
    const lastIdx = this.slides.length - 1;
    if (this.autoplayDirection === 'ltr') {
      if (this.activeIdx <= 0) {
        if (!this.autoplayLoop) {
          this.pauseAutoplay();
          return;
        }
        this.wrapToLast();
      } else {
        this.select(this.activeIdx - 1);
      }
    } else {
      if (this.activeIdx >= lastIdx) {
        if (!this.autoplayLoop) {
          this.pauseAutoplay();
          return;
        }
        this.wrapToFirst();
      } else {
        this.select(this.activeIdx + 1);
      }
    }
  }

  centerOffsetFor(slide) {
    return slide.offsetLeft - (this.viewport.clientWidth - slide.clientWidth) / 2;
  }

  select(idx, options) {
    if (!this.isSliderMode()) return;
    const slide = this.slides[idx];
    if (!slide || !this.viewport) return;
    const targetLeft = this.centerOffsetFor(slide);
    if (options && options.instant) {
      this.snapTo(targetLeft);
    } else {
      this.animateScrollTo(targetLeft, this.transitionMs);
    }
    this.activeIdx = idx;
    this.setActiveThumb(idx);
  }

  wrapToFirst() {
    if (!this.ghostEnd || !this.viewport) {
      this.select(0);
      return;
    }
    const ghostLeft = this.centerOffsetFor(this.ghostEnd);
    this.animateScrollTo(ghostLeft, this.transitionMs, () => {
      this.snapTo(this.centerOffsetFor(this.slides[0]));
      this.activeIdx = 0;
      this.setActiveThumb(0);
    });
  }

  wrapToLast() {
    const lastIdx = this.slides.length - 1;
    if (!this.ghostStart || !this.viewport) {
      this.select(lastIdx);
      return;
    }
    const ghostLeft = this.centerOffsetFor(this.ghostStart);
    this.animateScrollTo(ghostLeft, this.transitionMs, () => {
      this.snapTo(this.centerOffsetFor(this.slides[lastIdx]));
      this.activeIdx = lastIdx;
      this.setActiveThumb(lastIdx);
    });
  }

  snapTo(left) {
    if (!this.viewport) return;
    this.viewport.style.scrollSnapType = 'none';
    this.viewport.scrollLeft = left;
    requestAnimationFrame(() => {
      this.viewport.style.scrollSnapType = '';
    });
  }

  animateScrollTo(targetLeft, duration, onComplete) {
    if (!this.viewport) return;
    cancelAnimationFrame(this.scrollAnimFrame);
    this.viewport.style.scrollSnapType = 'none';
    const startLeft = this.viewport.scrollLeft;
    const distance = targetLeft - startLeft;
    if (Math.abs(distance) < 1) {
      this.viewport.scrollLeft = targetLeft;
      this.viewport.style.scrollSnapType = '';
      if (onComplete) onComplete();
      return;
    }
    const startTime = performance.now();
    const step = (now) => {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / duration, 1);
      const eased = this.autoplayEasing === 'linear' ? t : 1 - Math.pow(1 - t, 3);
      this.viewport.scrollLeft = startLeft + distance * eased;
      if (t < 1) {
        this.scrollAnimFrame = requestAnimationFrame(step);
      } else {
        this.viewport.style.scrollSnapType = '';
        if (onComplete) onComplete();
      }
    };
    this.scrollAnimFrame = requestAnimationFrame(step);
  }

  setActiveThumb(idx) {
    this.thumbs.forEach((t, i) => {
      if (i === idx) t.setAttribute('aria-current', 'true');
      else t.removeAttribute('aria-current');
    });
  }

  disconnectedCallback() {
    this.pauseAutoplay();
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
  }
}

if (!customElements.get('ux-product')) {
  customElements.define('ux-product', UxProduct);
}
